import { useEffect } from 'react'
import {
  decorate,
  configure,
  action,
  observable,
  get,
  set,
  remove,
  toJS,
  autorun,
} from 'mobx'
import { debounce, merge, isEqual } from 'lodash'

// core
import * as config from 'config'
import { createId } from '_core/utils/createId'
import { makeFetch } from '_core/utils/makeFetch'

configure({ enforceActions: 'observed' })

const DEBOUNCE_DELAY = 200
const UNEXPECTED_RESPONSE_CODE = 'unexpected-format'
const UNEXPECTED_RESPONSE_MESSAGE = 'Unexpected response format'

const blankRequest = {
  id: null,
  method: null,
  params: null,
  components: null,
  resulted: null,
  result: null,
  effort: null,
  resolve: null,
  reject: null,
  promise: null,
};


export const Polling = (function () {
  // const _key = new WeakMap()

  class Polling {
    requests = {};
    batch = [];
    _lastNotice = null;

    static getDebugMessage = (message, item) => {
      const { method, components } = item
      const result = [`ERROR: ${message}`, `METHOD: ${method}`]

      if (components.length > 0) {
        result.push(`COMPONENTS: ${Object.keys(components).join(',')}`)
      }

      return result.join(' ')
    }

    _createRequest = ({ method, params, component }) => {
      checkNotificationsStart();
      
      const item = merge({}, blankRequest, {
        id: createId(method),
        method,
        params,
        effort: 1,
        components: component != null ? { [component]: true } : {},
      })

      item.promise = new Promise((resolve, reject) => {
        item.resolve = resolve
        item.reject = reject
      })

      return item
    }

    _getDuplicate = ({ method, params = null }) => {
      for (let id of this.batch) {
        const item = get(this.requests, id)

        if (item.method === method && isEqual(item.params, params)) {
          return item
        }
      }
    }

    query = ({ method, params, component }) => {
      const item = this._createRequest({ method, params, component })

      set(this.requests, item.id, item)
      this._doFetch(item.id)

      return item.promise
    }

    queryBatch = ({ method, params, component }) => {
      const dupl = this._getDuplicate({ method, params })
      let promise

      if (dupl != null) {
        component != null && set(dupl.components, component, true)
        promise = dupl.promise
      } else {
        const item = this._createRequest({ method, params, component })
        promise = item.promise

        set(this.requests, item.id, item)
        this.batch.push(item.id)
      }

      this.batch.length > 0 && this._debouncedDoFetchBatch()
      return promise
    }

    send = (method, params, component) =>
      this.query({ method, params, component })

    queue = (method, params, component) =>
      this.queryBatch({ method, params, component })

    _doWithCallback = func => async ({
      method,
      params,
      component,
      onSuccess,
      onFailure,
      onResult,
      waitResult,
      waitCallback,
    }) => {
      let result

      try {
        result = await func(method, params, component)

        if (typeof onSuccess === 'function') {
          waitResult || waitCallback
            ? await onSuccess(result)
            : onSuccess(result)
        }
      } catch (error) {
        result = error

        if (typeof onFailure === 'function') {
          waitResult || waitCallback
            ? await onFailure(result)
            : onFailure(result)
        }
      }

      if (typeof onResult === 'function') {
        waitResult ? await onResult(result) : onResult(result)
      }

      return result
    }

    sendAndDo = this._doWithCallback(this.send)
    queueAndDo = this._doWithCallback(this.queue)

    _getData = item => {
      const result = {
        method: item.method,
      }

      if (item.params != null && item.params.constructor === Object) {
        result.param = item.params
      }

      return result
    }

    _getListData = queue => ({
      list: queue.map(this._getData)
    })

    _reject = (item, error = {}) => {
      const {
        code = UNEXPECTED_RESPONSE_CODE,
        message = UNEXPECTED_RESPONSE_MESSAGE,
      } = error

      item.resulted = Date.now()
      item.result = {
        code,
        isInfo: error.code === config.API_ERROR.INSUFFICIENT_GOODS_QUANTITY,
        message,
        debug: Polling.getDebugMessage(message, item),
      }

      item.reject(toJS(item.result, { recurseEverything: true }))
      remove(this.requests, item.id)
    }

    _doFetch = id => {
      const item = get(this.requests, id)

      const data = {
        ...this._getData(item)
      }

      const fetchParams = {
        timeout: config.FETCH_SYNC_TIMEOUT,
        efforts: config.FETCH_SYNC_EFFORTS,
        retryDelay: config.FETCH_SYNC_RETRY_DELAY,

        onStart: () => {
        },

        onSuccess: result => {
          if (result == null || result.constructor !== Object) {
            process.env.NODE_ENV !== 'production' && console.error(result)
            this._reject(item)
          } else {
            this.answer(item, result)
          }
        },

        onError: error => {
          process.env.NODE_ENV !== 'production' && console.error(error)
          this._reject(item, error)
        },
      }

      makeFetch({
        data,
        preset: 'POST',
        params: fetchParams,
        url: `${config.API_URL_BASE}${config.API_URL.SYNC}`,
      })
    }

    _doFetchBatch = () => {
      this.batch.forEach(id => this._doFetch(id))
      this.batch = []
    }

    _debouncedDoFetchBatch = debounce(this._doFetchBatch, DEBOUNCE_DELAY, {
      maxWait: 5 * DEBOUNCE_DELAY,
    })

    answer = (item, result) => {
      if (result.code === 'ok') {
        item.resulted = Date.now()
        item.result = result.data

        item.resolve(toJS(item.result, { recurseEverything: true }))
        remove(this.requests, item.id)
      } else {
        if (result.code === config.API_ERROR.DATA_INCORRECT) {
          item.resulted = Date.now()
          item.result = {
            code: result.code,
            errors: result.data?.errors,
            message: result.message,
            debug: Polling.getDebugMessage(result.message, item),
          }

          item.reject(toJS(item.result, { recurseEverything: true }))
          remove(this.requests, item.id)
        } else {
          if (item.method !== config.API_METHOD.VISITOR_INFO && result.code === 'visitor-unauthorized')
            setTimeout(() => { this.doNotice(config.API_NOTICE.VISITOR_LOGOUT, null); }, 10);
          
          this._reject(item, result)
        }
      }
    }

    doNotice = (name, data) => {
      this._lastNotice = { name, data, time: Date.now() };
    };

    onNotice = (name, callback, once) => {
      if (!(name instanceof Set)) {
        if (!Array.isArray(name))
          name = [name];
        
        name = new Set(name);
      }
      
      return autorun((reaction) => {
        const notice = this._lastNotice;
        
        if (!notice || !name.has(notice.name))
          return;
        
        callback(notice.name, notice.data, notice.time);
        
        once && reaction.dispose();
      });
    };
    
    useNotice = (name, callback, observe = []) => {
      useEffect(() => this.onNotice(name, callback), observe);
    };
    
    get isNoticeActive() {
      return checkNotificationsRest > 0;
    }
  };

  decorate(Polling, {
    answer: action,
    query: action,
    queryBatch: action,
    doNotice: action,
    _doFetch: action,
    _doFetchBatch: action,
    _reject: action,
    _lastNotice: observable,
    requests: observable,
    batch: observable,
  });
  
  return new Polling();
})();


let checkNotificationsTimeout = null;
let checkNotificationsFirst = true;
let checkNotificationsLast = 0;
let checkNotificationsRest = config.FETCH_NOTICE_INACTIVE_COUNT;

const checkNotificationsStart = () => {
  if (checkNotificationsTimeout)
    return;
  
  checkNotificationsTimeout = setTimeout(checkNotifications, config.FETCH_NOTICE_MIN_DELAY);
  
  ['mousemove', 'mousedown', 'keydown', 'focus', 'wheel', 'scroll']
    .forEach((name) => window.addEventListener(name, () => checkNotificationsRest = config.FETCH_NOTICE_INACTIVE_COUNT));
};

const checkNotifications = async () => {
  try {
    const url = config.API_URL_BASE + config.API_URL.NOTICE + '?timestamp=' + checkNotificationsLast;
    const options = {
      method: 'GET',
      
      headers: {
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache'
      }
    };
    
    const response = await fetch(url, options);
    
    if (!response.ok)
      return;
    
    const data = await response.json();
    
    if (!Array.isArray(data?.notifications) || !data.notifications.length)
      return;
    
    checkNotificationsLast = data.last_timestamp;
    
    if (checkNotificationsFirst) {
      checkNotificationsFirst = false;
    } else {
      data.notifications.forEach((item) => Polling.doNotice(item.name, item.data));
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production')
      console.error(e);
  } finally {
    const timeout = checkNotificationsRest < 0 
      ? config.FETCH_NOTICE_MIN_DELAY 
        + (-checkNotificationsRest / config.FETCH_NOTICE_COOLING_DOWN_COUNT) 
        * (config.FETCH_NOTICE_MAX_DELAY - config.FETCH_NOTICE_MIN_DELAY) 
      : config.FETCH_NOTICE_MIN_DELAY;
    
    checkNotificationsTimeout = setTimeout(checkNotifications, timeout);
    
    checkNotificationsRest = Math.max(checkNotificationsRest - 1, -config.FETCH_NOTICE_COOLING_DOWN_COUNT);
  }
};
